import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "Buy on WhatsApp" link in sync with the selected variant.
 *
 * Horizon re-renders the section (product page) or the `section-rendering-product-card`
 * section (product cards) whenever a variant or swatch is picked and dispatches the standard
 * `productSelect` event with a promise that resolves to the parsed HTML. The link is rebuilt
 * from that HTML so the message text (variant title, price, URL) always comes from Liquid.
 *
 * As a safety net the variant ID is re-read from the surrounding form at click time.
 */
class WhatsAppBuyButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const target = this.closest('product-card, dialog, .shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.#link?.addEventListener('click', this.#onClick, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLAnchorElement | null} */
  get #link() {
    return this.querySelector('a');
  }

  /**
   * @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document, resource?: { id?: number | string } | null, productId?: string, newProduct?: { id: number | string } } }> }} event
   */
  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail) return;

        const { html, resource, productId, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (productId && productId !== this.dataset.productId) {
          return;
        }

        const fresh = html?.querySelector(`whatsapp-buy-button[data-context="${this.dataset.context}"] a`);

        if (fresh instanceof HTMLAnchorElement) {
          this.#applyFrom(fresh);
          return;
        }

        if (resource?.id) {
          this.#setVariantId(String(resource.id));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[whatsapp-buy] Event promise rejected:', error);
      });
  };

  /**
   * Copies the freshly rendered link's attributes onto the live link.
   * @param {HTMLAnchorElement} fresh
   */
  #applyFrom(fresh) {
    const link = this.#link;
    if (!link) return;

    const href = fresh.getAttribute('href');
    if (href) link.setAttribute('href', href);

    const ariaLabel = fresh.getAttribute('aria-label');
    if (ariaLabel) link.setAttribute('aria-label', ariaLabel);

    if (fresh.dataset.variantId) link.dataset.variantId = fresh.dataset.variantId;
  }

  /**
   * Fallback when no re-rendered link is available: swap the variant ID inside the encoded message URL.
   * @param {string} variantId
   */
  #setVariantId(variantId) {
    const link = this.#link;
    if (!link) return;

    const current = link.dataset.variantId;
    if (!current || current === variantId) return;

    const href = link.getAttribute('href') ?? '';
    link.setAttribute('href', href.split(`variant%3D${current}`).join(`variant%3D${variantId}`));
    link.dataset.variantId = variantId;
  }

  /**
   * Reads the variant the surrounding form or card currently has selected.
   * @returns {string | null}
   */
  #selectedVariantId() {
    const form =
      this.closest('product-form-component') ??
      this.closest('dialog, .shopify-section')?.querySelector('product-form-component');
    const input = form?.querySelector('input[name="id"]');

    if (input instanceof HTMLInputElement && input.value) return input.value;

    const card = /** @type {(HTMLElement & { getSelectedVariantId?: () => string | null }) | null} */ (
      this.closest('product-card')
    );

    return card?.getSelectedVariantId?.() ?? null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // Product cards navigate on any click inside the card; this link opens WhatsApp instead.
    event.stopPropagation();

    const selected = this.#selectedVariantId();
    if (selected) this.#setVariantId(selected);
  };
}

if (!customElements.get('whatsapp-buy-button')) {
  customElements.define('whatsapp-buy-button', WhatsAppBuyButton);
}
